import { ChevronRight, PlusCircle } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { CopyableText } from '@/components/coverage/shared/CopyableText';
import { Button } from '@/components/ui/button';

import { useCurrentFirm } from '../hooks/useCurrentFirm';

interface BreadcrumbItem {
  name: string;
  link?: string;
}

export const AppBreadcrumb: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { firm_id } = useParams<{ firm_id: string }>();
  const { firm } = useCurrentFirm();
  const path = location.pathname;

  // Helper to check if the current path matches a string or RegExp
  const matches = useCallback(
    (pattern: string | RegExp): boolean =>
      typeof pattern === 'string' ? path.includes(pattern) : pattern.test(path),
    [path],
  );

  // Determine context flags
  const isFirmDetailsContext =
    firm_id && matches(new RegExp(`^/firms/${firm_id}(?:/[^/]+)?$`));
  const isDemandConfigPage = matches('/demand-configurations');

  // Determine the label for the last breadcrumb item based on the path
  const lastBreadcrumbLabel = useMemo(() => {
    if (matches('/create') && !isDemandConfigPage) return 'Create New Firm';
    if (matches('/demand-configurations/create'))
      return 'Create Demand Configuration';
    if (matches('/demand-configurations/edit'))
      return 'Edit Demand Configuration';
    return '';
  }, [matches, isDemandConfigPage]);

  // Build breadcrumb items with memoization to avoid unnecessary recalculations
  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    const items: BreadcrumbItem[] = [{ name: 'Firms', link: '/firms' }];

    if (firm) {
      items.push({ name: firm.name, link: `/firms/${firm.id}` });
    }

    if (firm && isDemandConfigPage && !isFirmDetailsContext) {
      items.push({
        name: 'Demand Configurations',
        link: `/firms/${firm.id}/demand-configurations`,
      });
    }

    if (lastBreadcrumbLabel) {
      items.push({ name: lastBreadcrumbLabel });
    }

    return items;
  }, [firm, isDemandConfigPage, isFirmDetailsContext, lastBreadcrumbLabel]);

  // Show the "New Configuration" button only in demand configuration context (and not during create/edit)
  const showCreateDemandConfigButton =
    isDemandConfigPage &&
    !isFirmDetailsContext &&
    !matches('/create') &&
    !matches('/edit');

  return (
    <div className="h-14 flex items-center my-4">
      <nav
        className="flex justify-between items-center w-full"
        aria-label="Breadcrumb"
      >
        <div className="flex items-center gap-4">
          <ol className="flex items-center flex-wrap">
            {breadcrumbItems.map((item, idx) => (
              <li key={idx} className="flex items-center">
                {idx > 0 && (
                  <ChevronRight className="mx-1.5 h-3 w-3 text-gray-400" />
                )}
                {item.link ? (
                  <Link
                    to={item.link}
                    className="text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    {item.name}
                  </Link>
                ) : (
                  <span className="text-sm font-medium text-gray-500">
                    {item.name}
                  </span>
                )}
              </li>
            ))}
          </ol>

          {firm && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>ID:</span>
              <CopyableText value={firm.id} mono />
            </div>
          )}
        </div>

        {showCreateDemandConfigButton && (
          <Button
            onClick={() =>
              navigate(`/firms/${firm_id}/demand-configurations/create`)
            }
            variant="outline"
            size="sm"
            className="flex items-center gap-1.5 flex-shrink-0 ml-4"
          >
            <PlusCircle className="h-3.5 w-3.5" />
            New Configuration
          </Button>
        )}
      </nav>
    </div>
  );
};
